import React, { Children, useEffect, useMemo } from 'react';
import { graphql } from 'gatsby';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { MainLayout } from '../components/UI/Layout/Main/mainLayout';
import { Component } from '../components/Contentful/component';
import {
  collectionFiltersState,
  collectionListState,
  collectionSearchLoadingState,
} from '../utils/recoil/collection';
import { alertState } from '../utils/recoil/snackbar';
import { MicroDataHelmet } from '../components/Utils/MicroData/Utils/microDataHelmet';
import { returnErrMessage, returnLocalisedPath } from '../utils/globals';
import { collectionMicroData } from '../components/Utils/MicroData/Collection/collectionMicroData';
import NoozApi from '../services/NoozApi';
import FilterService from '../services/FilterService';

const returnCategoryShopItemsUrl = (id) => `/productcategory/${id}/products`;

export default ({ data, pageContext, location }) => {
  const page = data?.contentfulPage;

  const { allLocalSlugs, category } = pageContext;
  const siteUrl = data?.site?.siteMetadata?.siteUrl;

  const {
    components,
    layout,
    popup,
    seoMetadata,
    slug,
    title,
    canonical,
    robots,
  } = page;

  const [alert, setAlert] = useRecoilState(alertState);
  const setFilters = useSetRecoilState(collectionFiltersState);
  const setCollectionListState = useSetRecoilState(collectionListState);
  const setCollectionSearchLoading = useSetRecoilState(
    collectionSearchLoadingState
  );

  const locale = layout?.header?.node_locale.toLowerCase();
  const collectionUrl = `${siteUrl}/${
    process.env.GATSBY_ZONE
  }${returnLocalisedPath(locale, slug?.url || '')}`;

  // SEO Micro Data
  const returnCollectionMicroData = collectionMicroData(
    components,
    siteUrl,
    locale,
    collectionUrl,
    category,
    seoMetadata
  );

  useEffect(() => {
    setFilters(FilterService.fromURl());
  }, [location]);

  useEffect(() => {
    if (window.location.search !== '') {
      setCollectionSearchLoading(true);
    }

    // TODO: update category with url parameters
    NoozApi.get(returnCategoryShopItemsUrl(category.contentful_id))
      .then((response) => {
        const shopItems = response.data;
        setCollectionListState(shopItems);
      })
      .catch((err) => {
        // Error notification on filter change error
        setAlert({
          ...alert,
          show: true,
          type: 'error',
          title: returnErrMessage(err),
        });
      })
      .finally(() => {
        setCollectionSearchLoading(false);
      });
    return () => {
      setFilters({});
      setCollectionListState([]); // reset collection list state on unmount
    };
  }, []);

  const children = useMemo(
    () =>
      components
        .map((props) => {
          return <Component key={props.id} {...props} />;
        })
        .reverse(),
    [components]
  );

  return (
    <>
      <MicroDataHelmet schemaOrgJSONLD={returnCollectionMicroData} />
      <MainLayout
        {...layout}
        page={{ seoMetadata, slug, title, canonical, robots, allLocalSlugs }}
        popup={popup}
        isCollectionPage
      >
        {children}
      </MainLayout>
    </>
  );
};

export const pageQuery = graphql`
  query CollectionPageById($id: String!) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    contentfulPage(id: { eq: $id }) {
      ...PageData
    }
  }
`;
